import './AddInputModal.scss';
import { useCallback, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { MdOutlineClose } from 'react-icons/md';
import {
  greaterThanIgnoreTime,
  lessThanIgnoreTime
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Button } from '@Components/Button';
import { Radio } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Appearance, Design, Size } from '@Components/designConstants';
import { DPCalendar } from '@Components/DatePicker/DPCalendar';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';
import {
  DEFAULT_MAX_YEAR,
  DEFAULT_MIN_YEAR,
  FOCUS_OPTIONS
} from '@Components/DatePicker/DatePickerButton/DatePickerConstants';
import { DateRange } from '../../../utils/date-utils';
import {
  ControlVariableAggregationTypes,
  ControlVariableTypes,
  ControlVariableUnitTypes,
  invalidOfflineInputsDates
} from './OfflineInputsUtils';

interface AddControlVariableProps {
  show: boolean;
  handleClose: () => void;
  createTable: (
    name: string,
    description: string,
    unitType: string,
    dataAggregation: string,
    startDate: Date,
    endDate?: Date
  ) => void;
}

export const AddControlVariable = ({ show, handleClose, createTable }: AddControlVariableProps) => {
  const [focus, setFocus] = useState<(typeof FOCUS_OPTIONS)[number]>(FOCUS_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [controlVariableName, setControlVariableName] = useState('');
  const [controlVariableDescription, setControlVariableDescription] = useState('');
  const [controlVariableType, setControlVariableType] = useState<ControlVariableTypes | undefined>(
    undefined
  );
  const [unitTypeCategory, setUnitTypeCategory] = useState<ControlVariableUnitTypes | undefined>(
    undefined
  );
  const [unitTypeStr, setUnitTypeStr] = useState('');
  const [dataAggregation, setDataAggregation] = useState<
    ControlVariableAggregationTypes | undefined
  >(undefined);
  const [dateRange, setDateRange] = useState<Partial<DateRange>>();

  const resetState = useCallback(() => {
    setIsSubmitting(false);
    setControlVariableName('');
    setControlVariableDescription('');
    setControlVariableType(undefined);
    setUnitTypeCategory(undefined);
    setUnitTypeStr('');
    setDataAggregation(undefined);
    setDateRange(undefined);
    setFocus(FOCUS_OPTIONS[0]);
  }, []);

  const isSubmitDisabled =
    isSubmitting ||
    !controlVariableName ||
    !controlVariableDescription ||
    !controlVariableType ||
    !dataAggregation ||
    !unitTypeCategory ||
    !unitTypeStr ||
    !dateRange?.start ||
    (dateRange.end
      ? lessThanIgnoreTime(new Date(dateRange.end), new Date(dateRange.start), false)
      : false);

  const onCalendarChange = (
    newDateRange: Partial<DateRange>,
    newFocus: (typeof FOCUS_OPTIONS)[number]
  ) => {
    setDateRange(newDateRange);
    setFocus(newFocus);
  };

  return (
    <Modal
      className='add-input-modal add-control-variable-modal'
      show={show}
      onHide={() => {
        handleClose();
        resetState();
      }}
      size='xl'
      centered
    >
      <Modal.Header>
        <h4 className='modal-title'>Add Control Variable</h4>
        <div className='sub-title'>*Required fields marked with an asterisk.</div>
        <div className='sub-title note'>
          Note: If your control variable cannot be captured as either binary (yes/no) or continuous
          (numeric), please contact us and we’ll add it for you.
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className='control-variable-column' id='control-variable-name-and-description-col'>
          <div className='control-variable-name-input'>
            <div className='section-title'>
              Control Variable Name<span className='asterisk'>*</span>
            </div>
            <Form.Control
              placeholder='Ex: PR Release'
              value={controlVariableName}
              onChange={e => setControlVariableName(e.target.value)}
            />
            <div className='input-explanation'>
              <div className='explanation'>
                <p>
                  Enter the name of a factor that affects business outcomes (not paid media, KPIs,
                  or promotions).
                </p>
              </div>
            </div>
          </div>
          <div className='control-variable-description-input'>
            <div className='section-title'>
              Description<span className='asterisk'>*</span>
            </div>
            <Form.Control
              as='textarea'
              id='control-variable-description-text-area'
              placeholder='Ex: A PR Release we believe caused a temporary sales boost.'
              value={controlVariableDescription}
              onChange={e => setControlVariableDescription(e.target.value)}
            />
            <div className='input-explanation'>
              <div className='explanation'>
                <p>
                  Briefly describe how you expect this event to impact your business. Was it a
                  one-time effect (ex: press coverage) or a lasting shift (ex: acquisition)?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='control-variable-column'>
          <div className='control-variable-type-input'>
            <div className='section-title'>
              Control Variable Type<span className='asterisk'>*</span>
            </div>
            <div className='top-radios'>
              <div id='binary-type'>
                <Radio
                  label='Binary'
                  checked={controlVariableType === ControlVariableTypes.BINARY}
                  id='binary'
                  onChange={() => {
                    setControlVariableType(ControlVariableTypes.BINARY);
                    setUnitTypeCategory(ControlVariableUnitTypes.BINARY);
                    setUnitTypeStr(ControlVariableUnitTypes.BINARY);
                    setDataAggregation(ControlVariableAggregationTypes.MAX);
                  }}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>
                      Use for yes/no factors like business merger, product launch, PR release. Data
                      will be entered as 0 (no) or 1 (yes).
                    </p>
                  </div>
                </div>
              </div>
              <div id='continuous-type'>
                <Radio
                  label='Continuous'
                  checked={controlVariableType === ControlVariableTypes.CONTINUOUS}
                  id='continuous'
                  onChange={() => {
                    setControlVariableType(ControlVariableTypes.CONTINUOUS);
                    setUnitTypeCategory(undefined);
                    setUnitTypeStr('');
                    setDataAggregation(undefined);
                  }}
                />
                <div className='input-explanation'>
                  <div className='explanation'>
                    <p>
                      Use for measurable factors with ranges, like weather, pricing, brand health
                      data, or competitor ad spend. Data will be entered as varied numbers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {controlVariableType === ControlVariableTypes.CONTINUOUS && (
              <div className='middle-radios'>
                <Radio
                  label='Percentage'
                  checked={unitTypeCategory === ControlVariableUnitTypes.PERCENTAGE}
                  id='percentage'
                  onChange={() => {
                    setControlVariableType(ControlVariableTypes.CONTINUOUS);
                    setUnitTypeCategory(ControlVariableUnitTypes.PERCENTAGE);
                    setUnitTypeStr(ControlVariableUnitTypes.PERCENTAGE);
                    setDataAggregation(ControlVariableAggregationTypes.AVERAGE);
                  }}
                />
                <Radio
                  label='Other'
                  checked={unitTypeCategory === ControlVariableUnitTypes.OTHER}
                  id='otherUnit'
                  onChange={() => {
                    setControlVariableType(ControlVariableTypes.CONTINUOUS);
                    setUnitTypeCategory(ControlVariableUnitTypes.OTHER);
                    setUnitTypeStr('');
                    setDataAggregation(undefined);
                  }}
                />
              </div>
            )}
            {unitTypeCategory === ControlVariableUnitTypes.OTHER && (
              <div id='unit-type-other-sub-menu'>
                <Form.Control
                  placeholder='Ex: Index'
                  value={unitTypeCategory === ControlVariableUnitTypes.OTHER ? unitTypeStr : ''}
                  onChange={e => setUnitTypeStr(e.target.value)}
                />
                <div className='data-aggregation-container'>
                  <div className='data-aggregation-title'>
                    Data aggregation for this unit{' '}
                    <InfoTooltip size={Size.MD}>
                      <ul id='dataAggregationTooltip'>
                        <li>
                          <strong>Total</strong>: Use this when you want the sum of all values in
                          the time period (e.g., weekly ad spend, total clicks, total conversions).
                        </li>
                        <li>
                          <strong>Average</strong>: Use this when you want the typical value over
                          the time period (e.g., average daily temperature, average brand health
                          score).
                        </li>
                      </ul>
                    </InfoTooltip>
                  </div>
                  <div className='data-aggregation-body'>
                    <Radio
                      label='Sum'
                      disabled={unitTypeCategory !== ControlVariableUnitTypes.OTHER}
                      checked={
                        unitTypeCategory === ControlVariableUnitTypes.OTHER &&
                        dataAggregation === ControlVariableAggregationTypes.SUM
                      }
                      id='sum'
                      onChange={() => {
                        setDataAggregation(ControlVariableAggregationTypes.SUM);
                      }}
                    />
                    <Radio
                      label='Average'
                      disabled={unitTypeCategory !== ControlVariableUnitTypes.OTHER}
                      checked={
                        unitTypeCategory === ControlVariableUnitTypes.OTHER &&
                        dataAggregation === ControlVariableAggregationTypes.AVERAGE
                      }
                      id='average'
                      onChange={() => {
                        setDataAggregation(ControlVariableAggregationTypes.AVERAGE);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='control-variable-column'>
          <div className='time-frame'>
            <div className='section-title'>
              Time Frame<span className='asterisk'>*</span>
            </div>
            <div className='top-container' id='control-variable-top-container'>
              <div className='date-container'>
                <DateInputField
                  label='Start Date'
                  value={dateRange?.start || ''}
                  setValue={newStartDate => {
                    setDateRange(prev => ({
                      ...prev,
                      start: newStartDate
                    }));
                  }}
                  invalidateDateOrRange={invalidOfflineInputsDates}
                  customValidation={[
                    {
                      validate: value => {
                        const parsedDate = new Date(value);
                        if (!dateRange?.end) {
                          return true;
                        }
                        return lessThanIgnoreTime(parsedDate, dateRange.end, true);
                      },
                      errorMessage: 'Start date must be before end date.'
                    }
                  ]}
                />
                <DateInputField
                  className='if-applicable-date-input-field'
                  label={
                    <div id='optional-end-date'>
                      End Date (if applicable)
                      <Button
                        size={Size.SM}
                        appearance={Appearance.OUTLINED}
                        leadingElement={<MdOutlineClose />}
                        onClick={() => setDateRange(prev => ({ ...prev, end: undefined }))}
                      />
                    </div>
                  }
                  value={dateRange?.end || ''}
                  setValue={newEndDate => {
                    setDateRange(prev => ({
                      ...prev,
                      end: newEndDate
                    }));
                  }}
                  invalidateDateOrRange={invalidOfflineInputsDates}
                  customValidation={[
                    {
                      validate: value => {
                        const parsedDate = new Date(value);
                        if (!dateRange?.start) {
                          return true;
                        }
                        return greaterThanIgnoreTime(parsedDate, dateRange.start, true);
                      },
                      errorMessage: ''
                    }
                  ]}
                />
              </div>
              <div className='input-explanation'>
                <div className='explanation'>
                  <p>
                    Only leave the end date blank when the control variable represents an ongoing
                    change to the baseline (ex: a partnership that continues to lift sales over
                    time).
                  </p>
                </div>
              </div>
            </div>
            <div className='bottom-container'>
              <DPCalendar
                start={dateRange?.start}
                end={dateRange?.end}
                focus={focus}
                onChange={onCalendarChange}
                years={Array.from(
                  { length: DEFAULT_MAX_YEAR + 1 - DEFAULT_MIN_YEAR },
                  (_, i) => DEFAULT_MIN_YEAR + i
                )}
                currentMonthSecond={false}
                numVisibleMonths={2}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (
              isSubmitting ||
              !controlVariableName ||
              !controlVariableDescription ||
              !dataAggregation ||
              !controlVariableType ||
              !unitTypeCategory ||
              !unitTypeStr ||
              !dateRange?.start ||
              (dateRange.end
                ? lessThanIgnoreTime(new Date(dateRange.end), new Date(dateRange.start), false)
                : false)
            ) {
              return;
            }
            setIsSubmitting(true);
            await createTable(
              controlVariableName,
              controlVariableDescription,
              unitTypeStr,
              dataAggregation,
              new Date(dateRange.start),
              dateRange.end ? new Date(dateRange.end) : undefined
            );
            handleClose();
            resetState();
          }}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
